import apiClient from './client';

export interface Reference {
  id: number;
  name: string;
  email: string;
  phone?: string;
  relationshipType: string;
  relationshipDescription?: string;
  yearsKnown?: number;
  organization?: string;
  position?: string;
  contactedByHub: boolean;
  contactedByHubAt?: string;
  contactedByHubUserId?: number;
  contactedByHubNotes?: string;
  contactedByUser?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface TheologicalSurvey {
  id: number;
  userId: number;
  churchPlanterId?: number;
  portalCandidateId?: number;
  scriptureAffirmation?: string;
  trinityUnderstanding?: string;
  originalSinUnderstanding?: string;
  christologyUnderstanding?: string;
  salvationDoctrines?: string;
  gospelPresentation?: string;
  churchNatureMission?: string;
  churchOrdinances?: string;
  churchMembership?: string;
  churchOffices?: string;
  eschatologicalConvictions?: string;
  eschatologyMinistryImpact?: string;
  believersBaptism?: string;
  churchAutonomy?: string;
  preachingPhilosophy?: string;
  discipleMaking?: string;
  worshipPhilosophy?: string;
  evangelismSocialEngagement?: string;
  theologicalDisagreement?: string;
  gospelPluralisticCulture?: string;
  contextualizationView?: string;
  plantingChallenges?: string;
  personalHoliness?: string;
  marriageFamilyProtection?: string;
  conflictHandling?: string;
  isComplete?: boolean;
  submittedAt?: string;
  reviewedBy?: number;
  reviewedAt?: string;
  reviewNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SpiritualHealthSurvey {
  id: number;
  userId: number;
  portalCandidateId: number;
  surveyFor: 'candidate' | 'spouse';
  // SECTION 1: Spiritual Health Awareness
  currentWalkWithLord?: string;
  intimacyWithChristHindrances?: string;
  spiritualDrynessResponse?: string;
  soulGuarding?: string;
  restAndSabbathRhythms?: string;
  recentConvictionResponse?: string;
  // SECTION 2: Emotional Health Awareness
  emotionalStabilityDescription?: string;
  pressureCriticismResponse?: string;
  stressDisappointmentHandling?: string;
  healthyBoundaries?: string;
  currentEmotionalState?: string;
  burnoutPrevention?: string;
  // SECTION 3: Calling and Resilience
  godsCallDescription?: string;
  callConfirmationByOthers?: string;
  difficultMoments?: string;
  discerningGodsLeading?: string;
  faithAndWisdomBalance?: string;
  churchPlantingFears?: string;
  // SECTION 4: Relational Health Awareness
  feedbackCorrectionResponse?: string;
  conflictResolutionApproach?: string;
  shepherdingDifficultPeople?: string;
  fosteringUnity?: string;
  relationalIntegrity?: string;
  accountabilityRelationships?: string;
  // SECTION 5: Spiritual Warfare Health Awareness
  spiritualAttackRecognition?: string;
  prayerInDecisions?: string;
  guardingAgainstCynicism?: string;
  godlyProvisionExample?: string;
  prayingTogether?: string;
  sustainingScriptures?: string;
  // SECTION 6: Accountability Health Awareness
  pastorMentorAccountability?: string;
  discipleshipFrequency?: string;
  teachability?: string;
  isolationPrevention?: string;
  spouseSupportNeeds?: string;
  handlingUnappreciation?: string;
  // Additional tracking fields
  status?: 'draft' | 'submitted';
  submittedAt?: string;
  reviewedBy?: number;
  reviewedAt?: string;
  reviewNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PortalCandidate {
  id: number;
  userId: number;
  name: string;
  portalStatus?: string;
  onboardingStep: number;
  onboardingCompleted: boolean;
  profileCompleted?: boolean;
  theologyCompleted?: boolean;
  referencesCompleted?: boolean;
  sendingChurchCompleted?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  maritalStatus?: string;
  wifeFirstName?: string;
  wifeLastName?: string;
  wifeEmail?: string;
  wifePhone?: string;
  childrenNamesAges?: string;
  targetLocationMississippi?: string;
  willBeSouthernBaptist?: string;
  ministryLeadershipPositions?: string;
  ministryInfluences?: string;
  theologicalTraining?: string;
  sermonReferenceUrl?: string;
  headshotImagePath?: string;
  familyPhotoImagePath?: string;
  // Prepare/Enrich fields
  prepareEnrichRequested?: boolean;
  prepareEnrichRequestedAt?: string;
  prepareEnrichStatus?: 'not_started' | 'started' | 'scheduled' | 'complete';
  prepareEnrichStatusUpdatedAt?: string;
  prepareEnrichNotes?: string;
  prepareEnrichData?: any;
  // Background Check fields
  backgroundCheckSubmitted?: boolean;
  backgroundCheckSubmittedAt?: string;
  backgroundCheckStatus?: 'not_started' | 'started' | 'submitted' | 'received';
  backgroundCheckStatusUpdatedAt?: string;
  backgroundCheckNotes?: string;
  backgroundCheckData?: any;
  // Split progress (backend-calculated)
  portalProgressPercentage?: number;
  teamProgressPercentage?: number;
  // Why Church Planting fields
  whyChurchPlantingCompleted?: boolean;
  whyChurchPlantingData?: Record<string, any> | null;
  // Spouse Interview fields
  spouseInterviewSubmitted?: boolean;
  spouseInterviewSubmittedAt?: string;
  spouseInterviewData?: Record<string, any> | null;
  spouseInterviewStatus?: string;
  spouseInterviewStatusUpdatedAt?: string;
  spouseInterviewNotes?: string;
  // Ministry Transitions fields
  ministryTransitionsCompleted?: boolean;
  ministryTransitionsData?: Record<string, any> | null;
  // Spiritual/Emotional Health fields
  spiritualHealthCompleted?: boolean;
  spiritualHealthCandidateSubmitted?: boolean;
  spiritualHealthSpouseSubmitted?: boolean;
  spiritualHealthNotes?: string;
  spiritualHealthSurveys?: SpiritualHealthSurvey[];
  // Theological Survey data
  theologicalSurvey?: TheologicalSurvey;
  // Sending Church
  sendingChurch?: {
    churchName?: string | null;
    primaryContactName?: string | null;
    primaryContactPhone?: string | null;
    noSendingChurch?: boolean;
    noSendingChurchExplanation?: string | null;
  } | null;
  user?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  assessmentOutcome?: 'red' | 'yellow' | 'green' | null;
  assessmentOutcomeAt?: string | null;
  assessmentOutcomeNotes?: string | null;
  migratedToChurchPlanterId?: number | null;
  createdAt: string;
  updatedAt: string;
}

// For now, we'll need to add an admin endpoint to the backend
// This is a placeholder that will work once the backend endpoint is added
export const getOnboardingCandidates = async (params?: {
  status?: string;
  /** Pre-grouped portal statuses (see pipeline overview buckets) */
  portalBucket?: string;
  /** Exclude users who already have a church_planter row */
  prePlanterOnly?: boolean;
  page?: number;
  limit?: number;
}): Promise<{
  success: boolean;
  data: PortalCandidate[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}> => {
  const { prePlanterOnly, ...rest } = params || {};
  const query = {
    ...rest,
    ...(prePlanterOnly ? { prePlanterOnly: 'true' as const } : {}),
  };
  const response = await apiClient.get('/admin/portal-candidates', { params: query });
  return response.data;
};

export const getOnboardingCandidate = async (id: number): Promise<PortalCandidate> => {
  const response = await apiClient.get(`/admin/portal-candidates/${id}`);
  return response.data.data;
};

export const updateCandidateStatus = async (id: number, status: string): Promise<PortalCandidate> => {
  const response = await apiClient.put(`/admin/portal-candidates/${id}/status`, { status });
  return response.data.data;
};

export const deleteCandidate = async (id: number): Promise<void> => {
  await apiClient.delete(`/admin/portal-candidates/${id}`);
};

export const markCandidateGreen = async (id: number, options?: {
  assignedCoach?: number;
  assignedHub?: string;
  assignedHubLeader?: number;
}): Promise<{ success: boolean; data: any; message?: string }> => {
  const response = await apiClient.post(`/admin/portal-candidates/${id}/mark-green`, options || {});
  return response.data;
};

export const markCandidateRed = async (
  id: number,
  notes?: string,
): Promise<{ success: boolean; data: PortalCandidate; message?: string }> => {
  const response = await apiClient.post(`/admin/portal-candidates/${id}/mark-red`, { notes });
  return response.data;
};

export const markCandidateYellow = async (
  id: number,
  notes?: string,
): Promise<{ success: boolean; data: PortalCandidate; message?: string }> => {
  const response = await apiClient.post(`/admin/portal-candidates/${id}/mark-yellow`, { notes });
  return response.data;
};

export const putCandidateOnGrowthPlan = async (
  id: number,
  options?: { assignedCoach?: number; assignedHub?: string; assignedHubLeader?: number },
): Promise<{ success: boolean; data: any; message?: string }> => {
  const response = await apiClient.post(`/admin/portal-candidates/${id}/put-on-growth-plan`, options || {});
  return response.data;
};

// Reference management
export const getCandidateReferences = async (candidateId: number): Promise<Reference[]> => {
  const response = await apiClient.get(`/admin/portal-candidates/${candidateId}/references`);
  return response.data.data;
};

export const markReferenceContacted = async (
  candidateId: number, 
  referenceId: number, 
  notes?: string
): Promise<{ success: boolean; data: Reference; allReferencesContacted: boolean }> => {
  const response = await apiClient.put(
    `/admin/portal-candidates/${candidateId}/references/${referenceId}/contacted`,
    { notes }
  );
  return response.data;
};

export const unmarkReferenceContacted = async (
  candidateId: number, 
  referenceId: number
): Promise<{ success: boolean; data: Reference }> => {
  const response = await apiClient.delete(
    `/admin/portal-candidates/${candidateId}/references/${referenceId}/contacted`
  );
  return response.data;
};


